import { useAuthContext } from "../../auth/state/AuthContext";
import { fetchDataWithParams, postData } from "../../shared/services/service";
import { updateVoyageChecklistCountAtom, voyageChecklistTabLabels } from "../atoms/checklistCounts.atoms";
import { InitialChecks, OptionType, TaskItem } from "../models/delivery-plan.interfaces";
import { PlanChecklist } from "./PlanChecklist";
import { datadogRum } from "@datadog/browser-rum";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SingleValue } from "react-select";

const staticOptions: OptionType[] = [
  {
    value: "Daily ETA notices received/ETA updated in IMOS",
    label: "Daily ETA notices received/ETA updated in IMOS",
  },
  {
    value: "Trader alerted if any doubt on vessel arrival within the laycan",
    label: "Trader alerted if any doubt on vessel arrival within the laycan",
  },
];

const VoyageToLoadPort = () => {
  const { firstname, lastname } = useAuthContext().state.user?.accessToken || {};
  const fullName = `${firstname} ${lastname}`;
  const { tcCode, voyageNo } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<TaskItem[]>([]);
  const [voyageToLoadPortCheckedItems, setVoyageToLoadPortCheckedItems] = useState<InitialChecks>({});
  const [options, setOptions] = useState<OptionType[]>(staticOptions);
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
  const [inputText, setInputText] = useState<string>("");
  const [showInput, setShowInput] = useState(false);
  const { sub: email } = useAuthContext().state.user?.accessToken || {};
  const updateChecklistCount = useSetAtom(updateVoyageChecklistCountAtom);
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value);
  };
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      const requestData = {
        tc_code: tcCode,
        voyage_no: voyageNo,
        key_activity: "Voyage to Load port",
      };
      const filteredRequestData = Object.fromEntries(
        Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
      ) as Record<string, string>;
      const queryParams = new URLSearchParams(filteredRequestData).toString();
      const result = await fetchDataWithParams(`getchecklist?${queryParams}`);
      const filteredData = result.data
        ?.filter((item: TaskItem) => !(!item.RECURRING_DISPLAY_DROPDOWN && item.TASK_TYPE === "Recurring"))
        .sort((a: TaskItem, b: TaskItem) => {
          // Sort by LAST_UPDATED_GMT, putting null or empty dates at the end
          const getDateValue = (item: TaskItem) => {
            const dateValue = item.LAST_UPDATED_GMT?.trim();
            return dateValue ? new Date(dateValue).getTime() : 0;
          };

          // Prioritize by LAST_UPDATED_GMT in descending order
          return getDateValue(b) - getDateValue(a);
        });
      setItems(filteredData);
      const initialCheckedState: { [key: number]: boolean } = {};
      filteredData.forEach((item: { TASK_ID: number; IS_CHECKED: string }) => {
        initialCheckedState[item.TASK_ID] = item.IS_CHECKED === "Y";
      });

      setVoyageToLoadPortCheckedItems(initialCheckedState);

      // Filter or disable options based on fetched data
      const updatedOptions: OptionType[] = staticOptions.map((staticOption) => {
        const matchingApiData = filteredData.find(
          (apiData: TaskItem) =>
            apiData.TASK_DESCRIPTION === staticOption.value && apiData.RECURRING_DISPLAY_DROPDOWN === "N",
        );

        return matchingApiData ? { ...staticOption, isDisabled: true } : staticOption;
      });

      setOptions(updatedOptions);
    } catch (error: unknown) {
    } finally {
      setIsLoading(false);
    }
  }, [tcCode, voyageNo]);

  const handleCheckboxChange = (taskId: number, item: TaskItem) => {
    setVoyageToLoadPortCheckedItems((prev) => {
      const isCurrentlyChecked = prev[taskId];
      const newCheckedState = {
        ...prev,
        [taskId]: !isCurrentlyChecked,
      };
      // Prepare the item with updated IS_CHECKED value
      const updatedItem = {
        ...item,
        IS_CHECKED: isCurrentlyChecked ? "N" : "Y",
      };

      // Send updated status to the API
      sendCheckboxStatus(updatedItem);

      return newCheckedState;
    });
  };

  const sendCheckboxStatus = async (item: TaskItem) => {
    try {
      const requestData = {
        tc_code: tcCode,
        voyage_no: voyageNo,
        task_name: item.TASK_DESCRIPTION,
        task_type: item.TASK_TYPE,
        operator: fullName,
        is_checked: item.IS_CHECKED,
        key_activity: item.KEY_ACTIVITY,
        is_deleted: "N",
        is_added: "N",
      };
      datadogRum.addAction("voyageCheckedTask", {
        ext_user_email: email,
        ...requestData,
      });
      const filteredRequestData = Object.fromEntries(
        Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
      ) as Record<string, string>;
      const queryParams = new URLSearchParams(filteredRequestData).toString();
      setIsLoading(true);

      const result = await postData(`modifytcvcchecklist?${queryParams}`, requestData);

      const output = result.data;
      if (output.success === true) {
        fetchData();
      }
    } catch (error: unknown) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAddButtonClick = async () => {
    if (!selectedOption) {
      console.error("No option selected");
      return;
    }
    try {
      setIsLoading(true);

      const requestData = {
        tc_code: tcCode,
        voyage_no: voyageNo,
        task_name: selectedOption.value,
        task_type: "Recurring",
        operator: fullName,
        is_checked: "N",
        key_activity: "Voyage to Load port",
        is_deleted: "N",
        is_added: "Y",
      };
      datadogRum.addAction("voyageCreateTask", {
        ext_user_email: email,
        ...requestData,
      });
      const filteredRequestData = Object.fromEntries(
        Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
      ) as Record<string, string>;
      const queryParams = new URLSearchParams(filteredRequestData).toString();
      const result = await postData(`modifytcvcchecklist?${queryParams}`, requestData);
      const output = result.data;
      if (output.success === true) {
        fetchData();
        setSelectedOption(null);
      }
    } catch (error: unknown) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectChange = (option: SingleValue<OptionType>) => {
    setSelectedOption(option as OptionType);
  };

  const extractInitials = (fullName: string | null): { firstNameInitial: string; lastNameInitial: string } => {
    const defaultInitials = { firstNameInitial: "", lastNameInitial: "" };
    if (!fullName) return defaultInitials;

    const nameArray = fullName.split(" ");
    const firstNameInitial = nameArray[0]?.charAt(0) ?? "";
    const lastNameInitial = nameArray.length > 1 ? nameArray[1]?.charAt(0) ?? "" : "";

    return { firstNameInitial, lastNameInitial };
  };

  const deliveryDeleteItem = async (item: TaskItem) => {
    try {
      setIsLoading(true);
      const requestData = {
        tc_code: tcCode,
        voyage_no: voyageNo,
        task_name: item.TASK_DESCRIPTION,
        task_type: item.TASK_TYPE,
        operator: fullName,
        is_checked: "N",
        key_activity: item.KEY_ACTIVITY,
        is_deleted: "Y",
        is_added: "N",
      };
      const filteredRequestData = Object.fromEntries(
        Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
      ) as Record<string, string>;
      const queryParams = new URLSearchParams(filteredRequestData).toString();

      const result = await postData(`modifytcvcchecklist?${queryParams}`, requestData);

      const output = result.data;
      if (output.success === true) {
        fetchData();
      }
    } catch (error: unknown) {
    } finally {
      setIsLoading(false);
    }
  };
  const VoyageToLoadPortResetToPreset = async (item: TaskItem) => {
    try {
      const requestData = {
        tc_code: tcCode,
        voyage_no: voyageNo,
        task_name: item.TASK_DESCRIPTION,
        task_type: item.TASK_TYPE,
        operator: fullName,
        key_activity: item.KEY_ACTIVITY,
      };
      const filteredRequestData = Object.fromEntries(
        Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
      ) as Record<string, string>;
      const queryParams = new URLSearchParams(filteredRequestData).toString();
      setIsLoading(true);

      const result = await postData(`resettopresenttcvcchecklist?${queryParams}`, requestData);

      const output = result.data;
      if (output.success === true) {
        fetchData();
      }
    } catch (error: unknown) {
    } finally {
      setIsLoading(false);
    }
  };
  const AddCustomInputTask = async () => {
    if (!inputText.trim()) return; // Avoid sending empty or just whitespace
    setSelectedOption(null);

    try {
      setIsLoading(true);

      const requestData = {
        tc_code: tcCode,
        voyage_no: voyageNo,
        task_name: inputText,
        task_type: "Custom",
        operator: fullName,
        is_checked: "N",
        key_activity: "Voyage to Load port",
        is_deleted: "N",
        is_added: "Y",
      };
      const filteredRequestData = Object.fromEntries(
        Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
      ) as Record<string, string>;
      const queryParams = new URLSearchParams(filteredRequestData).toString();

      const result = await postData(`modifytcvcchecklist?${queryParams}`, requestData);

      const output = result.data;
      if (output.success === true) {
        fetchData();
        setShowInput(false);
      }
    } catch (error: unknown) {
    } finally {
      setIsLoading(false);
    }
  };
  const customInputViewCancel = () => {
    setShowInput(false);
  };

  // Assuming VoyageToLoadPortCheckedItems is your state with the structure { [key: number]: boolean }
  const checkedCount = Object.values(voyageToLoadPortCheckedItems).filter((isChecked) => isChecked).length;
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    updateChecklistCount({
      tab: voyageChecklistTabLabels.voyageToLoadPort,
      checked: checkedCount,
      total: items.length,
    });
  }, [checkedCount, items.length, updateChecklistCount]);

  return PlanChecklist(
    isLoading,
    checkedCount,
    items,
    voyageToLoadPortCheckedItems,
    handleCheckboxChange,
    extractInitials,
    formatDate,
    VoyageToLoadPortResetToPreset,
    deliveryDeleteItem,
    showInput,
    handleInputChange,
    AddCustomInputTask,
    customInputViewCancel,
    options,
    setShowInput,
    handleSelectChange,
    selectedOption,
    handleSelectAddButtonClick,
  );
};

export default VoyageToLoadPort;
